import { Component, Input } from '@angular/core';

type StatusTone = 'ok' | 'wait' | 'busy' | 'bad' | 'idle';

/**
 * Status as plain text with a coloured dot. Colour is reserved for status
 * in the console, so the dot carries it and the label stays in ink.
 */
@Component({
  selector: 'app-status',
  standalone: true,
  template: `<span class="status" [attr.data-tone]="tone"><span class="status__dot" aria-hidden="true"></span><span class="status__label">{{ label }}</span></span>`,
  styles: `
    :host {
      display: inline-flex;
      min-width: 0;
    }

    .status {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
      font-family: var(--bk-font, "Comfortaa", sans-serif);
      font-size: 13px;
      font-weight: 500;
      white-space: nowrap;
      color: var(--bk-ink, currentColor);
    }

    .status__dot {
      flex-shrink: 0;
      width: 7px;
      height: 7px;
      border-radius: 999px;
      background: var(--tone);
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--tone) 18%, transparent);
    }

    .status__label {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    [data-tone="ok"] { --tone: var(--bk-ok, #16a34a); }
    [data-tone="wait"] { --tone: var(--bk-wait, #c98d00); }
    [data-tone="busy"] { --tone: var(--bk-busy, #2f7fd8); }
    [data-tone="bad"] { --tone: var(--bk-bad, #dc2626); }
    [data-tone="idle"] { --tone: var(--bk-idle, #a3a3a3); }
  `,
})
export class StatusComponent {
  @Input({ required: true }) text = 'Active';
  @Input({ required: true }) status: string = 'active';

  get tone(): StatusTone {
    const value = (this.status ?? '').toLowerCase();

    // Failure words first: "undeliverable" contains "deliver".
    if (/fail|error|danger|reject|expire|undeliver|deleted|skipped|bounce/.test(value)) return 'bad';
    if (/cancel|draft|disabled|stopped|archiv|inactive/.test(value)) return 'idle';
    if (/pending|schedul|queue|wait|pause/.test(value)) return 'wait';
    if (/complete|deliver|sent|success|done|finish/.test(value)) return 'ok';
    // active, in_progress, sending and anything unrecognised.
    return 'busy';
  }

  /** "in_progress" → "In progress", "COMPLETED" → "Completed"; "UNDELIVERABLE" reads "Failed". */
  get label(): string {
    const words = (this.text ?? '').replace(/[_-]+/g, ' ').trim().toLowerCase();
    if (/^undeliver(able|ed)?$/.test(words)) return 'Failed';
    return words ? words[0].toUpperCase() + words.slice(1) : '—';
  }
}
